import { useEffect, useRef } from 'react';
import i18n, { SUPPORTED_LANGUAGES, type SupportedLng } from '@/shared/lib/i18n';

/**
 * Jazyk (culture) kontrakt pro Magic control iframe — analogie dark-mode (viz [darkMode.ts](./darkMode.ts)).
 *
 * Jazyk legacy Magicu řídí Phoenix. Dva kanály:
 * - **Load-time**: URL param `culture=<cs|en>` (sestavuje [magicUrl.ts](./magicUrl.ts)) — Magic ho při
 *   OTT redeemu uloží do nastavení uživatele (stejně jako `SaveCulture` v Předvolbách) a vyrenderuje
 *   menu i formuláře v daném jazyce.
 * - **Runtime**: při přepnutí jazyka Phoenixu (i18next událost `languageChanged`) se control iframe
 *   znovu načte s novým paramem → Magic re-persistuje kulturu a znovu pošle `desktop/menu-ready`,
 *   takže se hlavní menu obnoví v novém jazyce (viz `useSyncLegacyCulture`).
 *
 * Phoenix i Magic používají shodné dvoupísmenné kódy (cs/en), takže mapování není potřeba.
 */

/** Aktuální jazyk Phoenixu jako Magic kultura (cs/en); fallback čeština. */
export function getActiveLegacyCulture(): SupportedLng {
  const base = (i18n.language ?? '').split('-')[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as SupportedLng) : 'cs';
}

/**
 * Zaregistruje reakci na runtime přepnutí jazyka: při i18next události `languageChanged` zavolá
 * `reload` (typicky re-mint OTT + přestavění control URL → reload skrytého control iframu).
 *
 * Initial mount nic neposílá — počáteční jazyk nese URL param `culture`, takže žádný falešný reload.
 */
export function useSyncLegacyCulture(reload: () => void): void {
  // Drží aktuální callback bez nutnosti re-subscribovat při změně jeho identity.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    const handler = () => reloadRef.current();
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);
}
